import { useEffect, useRef, useCallback } from 'react'

/**
 * Smart polling hook for counselor tasks/assignments
 * Features:
 * - Polls only when page is visible
 * - Exponential backoff when no changes detected
 * - Immediate refresh when page becomes visible
 * - Configurable intervals
 */
export const useTasksPolling = ({
	onUpdate,
	enabled = true,
	initialInterval = 30000, // 30 seconds
	maxInterval = 300000, // 5 minutes
	backoffMultiplier = 1.5,
}) => {
	const intervalRef = useRef(null)
	const currentIntervalRef = useRef(initialInterval)
	const lastTasksHashRef = useRef(null)
	const isPollingRef = useRef(false)
	const initialIntervalRef = useRef(initialInterval)
	const maxIntervalRef = useRef(maxInterval)
	const backoffMultiplierRef = useRef(backoffMultiplier)

	// Update refs when props change
	useEffect(() => {
		initialIntervalRef.current = initialInterval
		maxIntervalRef.current = maxInterval
		backoffMultiplierRef.current = backoffMultiplier
	}, [initialInterval, maxInterval, backoffMultiplier])

	const checkForUpdates = useCallback(async () => {
		if (isPollingRef.current) return

		try {
			isPollingRef.current = true
			const tasks = await onUpdate()

			// Create hash from tasks to detect changes
			// Hash includes: task IDs, response_text, submission_date, student_status
			const tasksHash = JSON.stringify(
				tasks.map(t => ({
					id: t.id || t.task_id,
					studentId: t.studentId,
					response_text: t.response_text,
					submission_date: t.submission_date,
					student_status: t.student_status,
				}))
			)

			// Check if tasks changed
			const hasChanged = lastTasksHashRef.current !== tasksHash

			if (hasChanged) {
				// Reset interval when changes detected
				currentIntervalRef.current = initialIntervalRef.current
				lastTasksHashRef.current = tasksHash
			} else {
				// Increase interval when no changes (exponential backoff)
				currentIntervalRef.current = Math.min(
					Math.round(currentIntervalRef.current * backoffMultiplierRef.current),
					maxIntervalRef.current
				)
			}
		} catch (error) {
			console.error('Failed to poll tasks:', error)
			// On error, use longer interval
			currentIntervalRef.current = Math.min(
				currentIntervalRef.current * 2,
				maxIntervalRef.current
			)
		} finally {
			isPollingRef.current = false
		}
	}, [onUpdate])

	const startPolling = useCallback(() => {
		if (!enabled) return

		// Clear existing interval
		if (intervalRef.current) {
			clearTimeout(intervalRef.current)
		}

		// Set up polling
		const scheduleNext = () => {
			intervalRef.current = setTimeout(() => {
				if (document.visibilityState === 'visible') {
					checkForUpdates()
					scheduleNext()
				} else {
					// If page is hidden, wait and check again
					intervalRef.current = setTimeout(scheduleNext, 5000)
				}
			}, currentIntervalRef.current)
		}

		scheduleNext()
	}, [enabled, checkForUpdates])

	const stopPolling = useCallback(() => {
		if (intervalRef.current) {
			clearTimeout(intervalRef.current)
			intervalRef.current = null
		}
	}, [])

	useEffect(() => {
		if (!enabled) {
			stopPolling()
			return
		}

		// Handle page visibility changes
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				// Page became visible - refresh immediately and restart polling
				currentIntervalRef.current = initialIntervalRef.current
				startPolling()
			} else {
				// Page hidden - stop polling to save resources
				stopPolling()
			}
		}

		// Handle window focus (user switches back to tab)
		const handleFocus = () => {
			if (document.visibilityState === 'visible') {
				currentIntervalRef.current = initialIntervalRef.current
				checkForUpdates()
				startPolling()
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)
		window.addEventListener('focus', handleFocus)

		// Start polling (don't call onUpdate immediately, let initial load handle it)
		startPolling()

		return () => {
			stopPolling()
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			window.removeEventListener('focus', handleFocus)
		}
	}, [enabled, startPolling, stopPolling, checkForUpdates])

	return {
		refresh: checkForUpdates,
		stopPolling,
		startPolling,
	}
}
